import React, { useState } from 'react';
import axios from 'axios';
import classNames from 'classnames';
import arrayMutators from 'final-form-arrays';
import { compose } from 'redux';
import { Form as FinalForm } from 'react-final-form';
import { useHistory, useLocation } from 'react-router-dom';

import { apiBaseUrl } from '../../../util/api';
import { propTypes } from '../../../util/types';
import * as validators from '../../../util/validators';
import { createResourceLocatorString } from '../../../util/routes';
import { getPropsForCustomUserFieldInputs } from '../../../util/userHelpers';
import { FormattedMessage, injectIntl, intlShape } from '../../../util/reactIntl';

import { useRouteConfiguration } from '../../../context/routeConfigurationContext';

import {
  Form,
  NamedLink,
  IconSpinner,
  PrimaryButton,
  FieldTextInput,
  FieldPhoneNumberInputWithDialCode,
} from '../../../components';

import css from './SignupWithUserTypeForm.module.css';
// import { ReactComponent as GoogleLogo } from '../../../assets/svg/googleLogo.svg';

const getSoleUserTypeMaybe = userTypes =>
  Array.isArray(userTypes) && userTypes.length === 1 ? userTypes[0].userType : null;

const SignupWithUserTypeFormComponent = props => {
  const history = useHistory();
  const location = useLocation();
  const routeConfiguration = useRouteConfiguration();
  const searchParams = new URLSearchParams(location.search);
  const { isEmailPresent, isSignupAsVendor, isSignupAsCustomer } = props;

  const phone = searchParams.get('phone');
  const isScreenVerifiyOTP = searchParams.get('verifyOTP');

  const [isVerifying, setIsVerifying] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [phoneForOTP, setPhoneForOTP] = useState({ phone: null });
  const [verifyPhoneNumberParagraph, setVerifyPhoneNumberParagraph] = useState('Sending OTP...');

  const sendOTP = async values => {
    try {
      setIsProcessing(true);
      setPhoneForOTP({ phone: values?.phoneNumber });
      const { dialCode, number } = values?.phoneNumber || {};
      const params = { verifyOTP: true, phone: JSON.stringify(values?.phoneNumber) };
      const res = await axios.post(`${apiBaseUrl()}/api/send-otp-v2`, {
        phoneNumber: `${dialCode}${number}`,
      });
      setIsProcessing(false);
      setVerifyPhoneNumberParagraph(res.data.data);

      if (isSignupAsCustomer) {
        history.push(
          createResourceLocatorString('SignupAsCustomerPage', routeConfiguration, {}, params)
        );
      } else {
        history.push(
          createResourceLocatorString('SignupAsVendorPage', routeConfiguration, {}, params)
        );
      }
    } catch (err) {
      console.error({ err });
      setIsProcessing(false);
      alert(err.response.data.data);
    }
  };

  const verifyOTP = async values => {
    try {
      setIsVerifying(true);
      const { otp, phoneNumber } = values || {};
      const { dialCode, number } = phoneNumber || {};
      const params = { phone: JSON.stringify(phoneNumber) };
      const res = await axios.post(`${apiBaseUrl()}/api/verify-otp-v2`, {
        otp,
        phoneNumber: `${dialCode}${number}`,
      });
      setIsVerifying(false);

      if (isSignupAsCustomer) {
        history.push(
          createResourceLocatorString('SignupAsCustomerPage', routeConfiguration, {}, params),
          { email: true }
        );
      } else {
        history.push(
          createResourceLocatorString('SignupAsVendorPage', routeConfiguration, {}, params),
          { email: true }
        );
      }
    } catch (err) {
      console.error({ err });
      setIsVerifying(false);
      alert(err.response.data.data);
    }
  };

  if (phoneForOTP.phone && isScreenVerifiyOTP) {
    return (
      <FinalForm
        {...props}
        mutators={{ ...arrayMutators }}
        initialValues={{
          userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes),
        }}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            formId,
            handleSubmit,
            inProgress = false,
            invalid,
            intl,
            preSelectedUserType,
            userTypes,
            userFields,
            values,
          } = formRenderProps;
          const { userType } = values || {};

          // Custom user fields. Since user types are not supported here,
          // only fields with no user type id limitation are selected.
          const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

          const noUserTypes = !userType && !(userTypes?.length > 0);
          const userTypeConfig = userTypes.find(config => config.userType === userType);
          const showDefaultUserFields = userType || noUserTypes;
          const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

          const submitInProgress = inProgress;
          const submitDisabled = invalid || submitInProgress;
          const classes = classNames(rootClassName || css.root, className);

          return (
            <Form
              className={classes}
              onSubmit={e => {
                e.preventDefault();
                verifyOTP(values);
              }}
            >
              <div className={css.container}>
                <div className={css.verifyOTP}>
                  <h4>
                    <FormattedMessage id="SignupForm.verifyPhoneNumberHeading" />
                  </h4>

                  <p>
                    <FormattedMessage
                      id="SignupForm.verifyPhoneNumberParagraph"
                      values={{
                        message: verifyPhoneNumberParagraph,
                      }}
                    />
                  </p>

                  <div className={css.verifyOTPForm}>
                    <div className={css.phone}>
                      <FieldTextInput
                        type="text"
                        id={formId ? `${formId}.otp` : 'otp'}
                        name="otp"
                        label={intl.formatMessage({
                          id: 'SignupForm.verifyPhoneNumberLabel',
                        })}
                        autoComplete="given-name"
                        placeholder={intl.formatMessage({
                          id: 'SignupForm.verifyPhoneNumberPlaceholder',
                        })}
                        validate={validators.required(
                          intl.formatMessage({
                            id: 'SignupForm.verifyPhoneNumberRequired',
                          })
                        )}
                      />
                    </div>

                    <div className={css.bottomWrapper}>
                      <PrimaryButton
                        // inProgress={submitInProgress}
                        type="submit"
                        inProgress={isVerifying}
                        disabled={submitDisabled || isVerifying}
                        className={css.signupButton}
                      >
                        {isVerifying ? (
                          <IconSpinner className={css.iconSpinner} />
                        ) : (
                          <FormattedMessage id="SignupForm.verifyPhoneNumberButtonText" />
                        )}
                      </PrimaryButton>
                    </div>
                  </div>
                </div>
              </div>
            </Form>
          );
        }}
      />
    );
  } else if (isEmailPresent) {
    return (
      <FinalForm
        {...props}
        mutators={{ ...arrayMutators }}
        initialValues={{
          userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes),
        }}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            form,
            formId,
            handleSubmit,
            inProgress,
            invalid,
            intl,
            userTypes,
            userFields,
            values,
            loginOrSignupError,
            termsAndConditions,
            preSelectedUserType,
          } = formRenderProps;
          const { userType } = values || {};

          // companyName
          const companyNameLabel = intl.formatMessage({
            id: 'SignupForm.companyNameLabel',
          });
          const companyNamePlaceholder = intl.formatMessage({
            id: 'SignupForm.companyNamePlaceholder',
          });
          const companyNameRequiredMessage = intl.formatMessage({
            id: 'SignupForm.companyNameRequired',
          });
          const companyNameRequired = validators.required(companyNameRequiredMessage);

          // companyEmail
          const companyEmailLabel = intl.formatMessage({
            id: 'ProfileSettingsForm.companyEmailLabel',
          });
          const companyEmailPlaceholder = intl.formatMessage({
            id: 'ProfileSettingsForm.companyEmailPlaceholder',
          });
          const companyEmailRequired = validators.required(
            intl.formatMessage({
              id: 'ProfileSettingsForm.companyEmailRequired',
            })
          );
          const companyEmailInvalid = validators.emailFormatValid(
            intl.formatMessage({
              id: 'ProfileSettingsForm.companyEmailInvalid',
            })
          );
          const companyEmailValidators = validators.composeValidators(
            companyEmailInvalid,
            companyEmailRequired
          );

          // email
          const emailRequired = validators.required(
            intl.formatMessage({
              id: 'SignupForm.emailRequired',
            })
          );
          const emailInvalid = validators.emailFormatValid(
            intl.formatMessage({
              id: 'SignupForm.emailInvalid',
            })
          );
          const emailValidators = validators.composeValidators(emailRequired, emailInvalid);

          // password
          const passwordRequiredMessage = intl.formatMessage({
            id: 'SignupForm.passwordRequired',
          });
          const passwordMinLengthMessage = intl.formatMessage(
            {
              id: 'SignupForm.passwordTooShort',
            },
            {
              minLength: validators.PASSWORD_MIN_LENGTH,
            }
          );
          const passwordMaxLengthMessage = intl.formatMessage(
            {
              id: 'SignupForm.passwordTooLong',
            },
            {
              maxLength: validators.PASSWORD_MAX_LENGTH,
            }
          );
          const passwordMinLength = validators.minLength(
            passwordMinLengthMessage,
            validators.PASSWORD_MIN_LENGTH
          );
          const passwordMaxLength = validators.maxLength(
            passwordMaxLengthMessage,
            validators.PASSWORD_MAX_LENGTH
          );
          const passwordRequired = validators.requiredStringNoTrim(passwordRequiredMessage);
          const validPassword = validators.validPassword('Do not contain any extra space');
          const passwordValidators = validators.composeValidators(
            passwordRequired,
            passwordMinLength,
            passwordMaxLength,
            validPassword
          );

          // confirm password
          const confirmPasswordRequired = validators.requiredStringNoTrim(
            intl.formatMessage({
              id: 'SignupForm.confirmPasswordRequired',
            })
          );
          const confirmPasswordInvalid = validators.isEqual(
            intl.formatMessage({
              id: 'SignupForm.confirmPasswordInvalid',
            }),
            values.password
          );
          const confirmPasswordValidators = validators.composeValidators(
            confirmPasswordRequired,
            confirmPasswordInvalid
          );

          // phoneNumber
          const phoneNumberRequired = validators.phoneRequired(
            intl.formatMessage({
              id: 'SignupForm.phoneNumberRequired',
            })
          );
          const phoneNumberInvalid = validators.phoneNumberFormatValid(
            intl.formatMessage({
              id: 'SignupForm.phoneNumberInvalid',
            })
          );
          const phoneNumberValidators = validators.composeValidators(
            phoneNumberRequired,
            phoneNumberInvalid
          );

          // Custom user fields. Since user types are not supported here,
          // only fields with no user type id limitation are selected.
          const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

          const noUserTypes = !userType && !(userTypes?.length > 0);
          const userTypeConfig = userTypes.find(config => config.userType === userType);
          const showDefaultUserFields = userType || noUserTypes;
          const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

          const submitInProgress = inProgress;
          const submitDisabled = invalid || submitInProgress;
          const classes = classNames(rootClassName || css.root, className);

          if (phone) {
            values.phoneNumber = JSON.parse(phone);
          }

          return (
            <Form className={classes} onSubmit={handleSubmit}>
              <div className={css.container}>
                {showDefaultUserFields ? (
                  <div className={css.signupFormContainer}>
                    <h2>
                      <FormattedMessage
                        id="SignupForm.registerFormHeading"
                        values={{
                          userType: isSignupAsVendor ? 'vendor' : 'customer',
                        }}
                      />
                    </h2>

                    {isSignupAsVendor && (
                      <div className={classNames(css.responsiveRow, css.companyNameAndEmail)}>
                        <FieldTextInput
                          type="text"
                          id="companyName"
                          name="companyName"
                          label={companyNameLabel}
                          placeholder={companyNamePlaceholder}
                          validate={companyNameRequired}
                        />
                        <FieldTextInput
                          type="email"
                          id={formId ? `${formId}.email` : 'email'}
                          name="email"
                          autoComplete="email"
                          label={companyEmailLabel}
                          placeholder={companyEmailPlaceholder}
                          validate={companyEmailValidators}
                        />
                      </div>
                    )}

                    <div className={classNames(css.name, css.responsiveRow)}>
                      <FieldTextInput
                        type="text"
                        id={formId ? `${formId}.fname` : 'fname'}
                        name="fname"
                        autoComplete="given-name"
                        label={intl.formatMessage({
                          id: 'SignupForm.firstNameLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'SignupForm.firstNamePlaceholder',
                        })}
                        validate={validators.required(
                          intl.formatMessage({
                            id: 'SignupForm.firstNameRequired',
                          })
                        )}
                      />
                      <FieldTextInput
                        type="text"
                        id={formId ? `${formId}.lname` : 'lname'}
                        name="lname"
                        autoComplete="family-name"
                        label={intl.formatMessage({
                          id: 'SignupForm.lastNameLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'SignupForm.lastNamePlaceholder',
                        })}
                        validate={validators.required(
                          intl.formatMessage({
                            id: 'SignupForm.lastNameRequired',
                          })
                        )}
                      />
                    </div>

                    {isSignupAsCustomer && (
                      <div className={css.email}>
                        <FieldTextInput
                          type="email"
                          id={formId ? `${formId}.email` : 'email'}
                          name="email"
                          autoComplete="email"
                          label={intl.formatMessage({
                            id: 'SignupForm.emailLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'SignupForm.emailPlaceholder',
                          })}
                          validate={emailValidators}
                        />
                      </div>
                    )}

                    <div className={classNames(css.secret, css.responsiveRow)}>
                      <FieldTextInput
                        type="password"
                        id={formId ? `${formId}.password` : 'password'}
                        name="password"
                        autoComplete="new-password"
                        label={intl.formatMessage({
                          id: 'SignupForm.passwordLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'SignupForm.passwordPlaceholder',
                        })}
                        validate={passwordValidators}
                      />
                      <FieldTextInput
                        type="password"
                        id={formId ? `${formId}.confirmPassword` : 'confirmPassword'}
                        name="confirmPassword"
                        autoComplete="new-password"
                        label={intl.formatMessage({
                          id: 'SignupForm.confirmPasswordLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'SignupForm.confirmPasswordPlaceholder',
                        })}
                        validate={confirmPasswordValidators}
                      />
                    </div>

                    <div className={classNames(css.responsiveRow)}>
                      {!phone && (
                        <FieldPhoneNumberInputWithDialCode
                          name="phoneNumber"
                          className={classNames({
                            [css.phoneRequired]: isSignupAsVendor,
                          })}
                          id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                          label={intl.formatMessage({
                            id: 'SignupForm.phoneNumberLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'SignupForm.phoneNumberPlaceholder',
                          })}
                          validate={isSignupAsVendor ? phoneNumberValidators : phoneNumberInvalid}
                        />
                      )}
                      {!phone && (
                        <FieldPhoneNumberInputWithDialCode
                          name="alternativePhoneNumber"
                          id={
                            formId ? `${formId}.alternativePhoneNumber` : 'alternativePhoneNumber'
                          }
                          label={intl.formatMessage({
                            id: 'SignupForm.alternativePhoneNumberLabel',
                          })}
                          placeholder={intl.formatMessage({
                            id: 'SignupForm.alternativePhoneNumberPlaceholder',
                          })}
                          validate={phoneNumberInvalid}
                        />
                      )}
                    </div>

                    <div>
                      <FieldTextInput
                        type="textarea"
                        id={formId ? `${formId}.bio` : 'bio'}
                        name="bio"
                        autoComplete="on"
                        label={intl.formatMessage({
                          id: isSignupAsCustomer
                            ? 'SignupForm.bioLabel'
                            : 'SignupForm.companyBioLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: isSignupAsCustomer
                            ? 'SignupForm.bioPlaceholder'
                            : 'SignupForm.companyBioPlaceholder',
                        })}
                      />
                    </div>
                  </div>
                ) : null}

                <div className={css.loginOrSignupError}>{loginOrSignupError}</div>

                <div className={css.bottomWrapper}>
                  {/* {termsAndConditions} */}
                  <PrimaryButton
                    type="submit"
                    disabled={submitDisabled}
                    inProgress={submitInProgress}
                    className={css.signupButton}
                  >
                    <FormattedMessage id="SignupForm.signUp" />
                  </PrimaryButton>
                </div>
              </div>
            </Form>
          );
        }}
      />
    );
  } else {
    return (
      <FinalForm
        {...props}
        mutators={{ ...arrayMutators }}
        initialValues={{
          userType: props.preselectedUserType || getSoleUserTypeMaybe(props.userTypes),
        }}
        render={formRenderProps => {
          const {
            rootClassName,
            className,
            form,
            formId,
            handleSubmit,
            inProgress,
            invalid,
            intl,
            termsAndConditions,
            preSelectedUserType,
            userTypes,
            userFields,
            values,
          } = formRenderProps;
          const { userType } = values || {};

          // phoneNumber
          const phoneNumberRequired = validators.phoneRequired(
            intl.formatMessage({
              id: 'SignupForm.phoneNumberRequired',
            })
          );
          const phoneNumberInvalid = validators.phoneNumberFormatValid(
            intl.formatMessage({
              id: 'SignupForm.phoneNumberInvalid',
            })
          );
          const phoneNumberValidators = validators.composeValidators(
            phoneNumberRequired,
            phoneNumberInvalid
          );

          // Custom user fields. Since user types are not supported here,
          // only fields with no user type id limitation are selected.
          const userFieldProps = getPropsForCustomUserFieldInputs(userFields, intl, userType);

          const noUserTypes = !userType && !(userTypes?.length > 0);
          const userTypeConfig = userTypes.find(config => config.userType === userType);
          const showDefaultUserFields = userType || noUserTypes;
          const showCustomUserFields = (userType || noUserTypes) && userFieldProps?.length > 0;

          const submitInProgress = inProgress;
          const submitDisabled = invalid || submitInProgress;
          const classes = classNames(rootClassName || css.root, className);

          return (
            <Form
              className={classes}
              onSubmit={e => {
                e.preventDefault();
                sendOTP(values);
              }}
            >
              <div className={css.container}>
                <div className={css.signupFormCustom}>
                  {isSignupAsVendor && (
                    <h2>
                      <FormattedMessage id="SignupForm.signupAsVendor" />
                    </h2>
                  )}
                  {isSignupAsCustomer && (
                    <h2>
                      <FormattedMessage id="SignupForm.signupAsCustomer" />
                    </h2>
                  )}

                  {/* <button type="button" className={css.signupWithGoogleBtn}>
                      <div>
                        <GoogleLogo />
                      </div>
                      Google
                    </button> */}

                  <div className={css.signupWithPhone}>
                    <div className={css.phone}>
                      <FieldPhoneNumberInputWithDialCode
                        name="phoneNumber"
                        className={css.phoneRequired}
                        id={formId ? `${formId}.phoneNumber` : 'phoneNumber'}
                        label={intl.formatMessage({
                          id: 'SignupForm.phoneNumberLabel',
                        })}
                        placeholder={intl.formatMessage({
                          id: 'SignupForm.phoneNumberPlaceholder',
                        })}
                        validate={phoneNumberValidators}
                      />
                    </div>

                    <div className={css.bottomWrapper}>
                      <PrimaryButton
                        // inProgress={submitInProgress}
                        type="submit"
                        inProgress={isProcessing}
                        disabled={submitDisabled || isProcessing}
                        className={css.signupButton}
                      >
                        {isProcessing ? (
                          <IconSpinner className={css.iconSpinner} />
                        ) : (
                          <FormattedMessage id="SignupForm.signUpWithPhone" />
                        )}
                      </PrimaryButton>
                    </div>
                  </div>

                  <div className={css.buttonDivider}>
                    <div></div>
                    <p>or continue with</p>
                    <div></div>
                  </div>

                  <PrimaryButton type="button" className={css.namedLinkButtonContainer}>
                    <NamedLink
                      to={{ state: { email: true } }}
                      className={css.namedLinkButton}
                      name={isSignupAsCustomer ? 'SignupAsCustomerPage' : 'SignupAsVendorPage'}
                    >
                      <FormattedMessage id="SignupForm.signUpWithEmail" />
                    </NamedLink>
                  </PrimaryButton>

                  <p className="terms">
                    <FormattedMessage
                      id="SignupForm.tosMsg"
                      values={{
                        tosLink: (
                          <a href="#">
                            <FormattedMessage id="SignupForm.tos" />
                          </a>
                        ),
                        privacyLink: (
                          <a href="#">
                            <FormattedMessage id="SignupForm.privacy" />
                          </a>
                        ),
                      }}
                    />
                  </p>
                </div>
              </div>
            </Form>
          );
        }}
      />
    );
  }
};

const SignupWithUserTypeForm = compose(injectIntl)(SignupWithUserTypeFormComponent);

export default SignupWithUserTypeForm;
